import type {
	Effort,
	MealDate,
	MealPlanDay,
	Recipe,
	RecipeId,
	Season,
	SystemTags,
} from '@hewey/shared'
import { totalMins } from '@apps/recipes/time.ts'
import { addDaysIso, daysBetween, isWeekend } from './dates.ts'
import {
	type Facets,
	type HistoryCandidate,
	historyCandidates,
	inferFacets,
	type MealAppearances,
	normaliseTitle,
} from './history.ts'

// How far either side of a day a meal must not already appear.
const REPEAT_WINDOW_DAYS = 7

// Cuisines within this many days of each other count as a clash.
const CUISINE_WINDOW_DAYS = 3

// Each facet score is centred on zero and stays roughly within [-1, 1], so
// these weights set their relative importance.
const W_DAY_FIT = 1
// A clash has to outweigh the jitter and the rotation bonus below, or the same
// carb lands two days running whenever the alternative happens to be a little
// fresher.
const W_PROTEIN_CLASH = 1.2
const W_CARB_CLASH = 1
const W_CUISINE_CLASH = 0.7
const W_SEASON_MISMATCH = 0.5
const W_ROTATION = 0.8
// A batch cook belongs on a Sat–Sun, where there's time for the big pot that
// covers a later meal. Above the jitter so it decides near-equal candidates,
// below a clash so it never costs the week its variety.
const W_BATCH_COOK = 0.5
// Enough to reorder near-equal candidates, not enough to put a low-scoring one
// first.
const W_JITTER = 0.35

// Used when a recipe has neither a published time nor an effort tag. Just
// under neutral, so it isn't pushed to Sat–Sun.
const UNKNOWN_HEAVINESS = 0.4

// Days since a recipe was last planned at which its rotation bonus reaches
// its maximum.
const ROTATION_FULL_DAYS = 60

// Facet values that say nothing, so they never count as a clash: two
// "unknown" proteins aren't the same protein.
const EMPTY_FACETS = new Set(['none', 'unknown', 'other'])

const known = (v: string): string | null => (EMPTY_FACETS.has(v) ? null : v)

const facetsOfTags = (t: SystemTags): Facets => ({
	protein: known(t.primaryProtein) as Facets['protein'],
	carb: known(t.primaryCarb) as Facets['carb'],
	cuisine: known(t.cuisine),
})

const clashes = (a: string | null, b: string | null): boolean =>
	a !== null && a === b

// 0 = quick and simple, 1 = long or involved.
const timeHeaviness = (mins: number): number =>
	mins <= 30
		? 0
		: mins <= 45
			? 0.25
			: mins <= 60
				? 0.5
				: mins <= 90
					? 0.75
					: 1

const EFFORT_HEAVINESS: Partial<Record<Effort, number>> = {
	low: 0,
	medium: 0.5,
	high: 1,
}

// Time and effort are independent — a traybake is low effort even if it roasts
// for an hour — so whichever are present are averaged.
const heavinessOf = (r: Recipe, tags: SystemTags): number => {
	const parts: number[] = []
	const mins = totalMins(r)
	if (mins !== null) parts.push(timeHeaviness(mins))
	const effort = EFFORT_HEAVINESS[tags.effort]
	if (effort !== undefined) parts.push(effort)
	if (parts.length === 0) return UNKNOWN_HEAVINESS
	return parts.reduce((a, b) => a + b, 0) / parts.length
}

// A meal that could be suggested for a day. Exactly one of `tags` (a tagged
// recipe) and `history` (a hand-typed meal) is set.
type Candidate = {
	key: string
	title: string
	recipeId: RecipeId | null
	tags: SystemTags | null
	facets: Facets
	heaviness: number | null
	history: HistoryCandidate | null
	lastPlanned: MealDate | null
}

// A meal already on a day, reduced to what the variety rules read.
type PlannedMeal = { key: string; facets: Facets }

type RecipeIndex = {
	byId: Map<RecipeId, Recipe>
	byTitleKey: Map<string, Recipe>
}

const indexRecipes = (recipes: Recipe[]): RecipeIndex => {
	const byId = new Map<RecipeId, Recipe>()
	const byTitleKey = new Map<string, Recipe>()
	for (const r of recipes) {
		byId.set(r.id, r)
		const key = normaliseTitle(r.title)
		if (!byTitleKey.has(key)) byTitleKey.set(key, r)
	}
	return { byId, byTitleKey }
}

const later = (a: MealDate | null, b: MealDate | null): MealDate | null =>
	a === null ? b : b === null ? a : a > b ? a : b

const buildPool = (
	recipes: Recipe[],
	appearances: Map<string, MealAppearances>,
	plan: MealPlanDay[],
	index: RecipeIndex,
): Candidate[] => {
	// A renamed recipe still has days linked to it by id, so the last-cooked
	// date is looked up by id and by title, and the later of the two is used.
	const lastById = new Map<RecipeId, MealDate>()
	for (const d of plan) {
		if (!d.recipeId) continue
		const prev = lastById.get(d.recipeId)
		if (prev === undefined || d.date > prev)
			lastById.set(d.recipeId, d.date)
	}

	const pool: Candidate[] = []
	const seen = new Set<string>()

	for (const r of recipes) {
		const tags = r.systemTags
		// Dinners only — the planner is one evening meal a day, and
		// `unknown` is too uncertain to include.
		if (r.taggingStatus !== 'tagged' || tags === null) continue
		if (tags.type !== 'dinner') continue
		const key = normaliseTitle(r.title)
		if (seen.has(key)) continue
		seen.add(key)
		pool.push({
			key,
			title: r.title,
			recipeId: r.id,
			tags,
			facets: facetsOfTags(tags),
			heaviness: heavinessOf(r, tags),
			history: null,
			lastPlanned: later(
				appearances.get(key)?.dates.at(-1) ?? null,
				lastById.get(r.id) ?? null,
			),
		})
	}

	for (const h of historyCandidates(
		appearances,
		new Set(index.byTitleKey.keys()),
	)) {
		pool.push({
			key: h.key,
			title: h.title,
			recipeId: null,
			tags: null,
			facets: h.facets,
			heaviness: null,
			history: h,
			lastPlanned: h.lastPlanned,
		})
	}

	return pool
}

const resolvePlanned = (
	day: MealPlanDay,
	index: RecipeIndex,
): PlannedMeal | null => {
	if (!day.title) return null
	const key = normaliseTitle(day.title)
	const recipe = day.recipeId
		? index.byId.get(day.recipeId)
		: index.byTitleKey.get(key)
	// A hand-typed day has no tags either, so it's read the same way a
	// hand-typed candidate is — otherwise it can't be clashed against.
	const tags = recipe?.systemTags
	return { key, facets: tags ? facetsOfTags(tags) : inferFacets(key) }
}

// Everything about one day that scoring needs, gathered once per day rather
// than once per candidate.
type DayContext = {
	weekend: boolean
	// Meals planned within the repeat window.
	nearbyKeys: Set<string>
	// The days either side.
	adjacent: PlannedMeal[]
	nearbyCuisines: Set<string>
}

const buildContext = (
	date: MealDate,
	planned: Map<MealDate, PlannedMeal>,
): DayContext => {
	const nearbyKeys = new Set<string>()
	const adjacent: PlannedMeal[] = []
	const nearbyCuisines = new Set<string>()
	for (let o = -REPEAT_WINDOW_DAYS; o <= REPEAT_WINDOW_DAYS; o++) {
		if (o === 0) continue
		const near = planned.get(addDaysIso(date, o))
		if (!near) continue
		nearbyKeys.add(near.key)
		if (Math.abs(o) === 1) adjacent.push(near)
		if (Math.abs(o) <= CUISINE_WINDOW_DAYS && near.facets.cuisine) {
			nearbyCuisines.add(near.facets.cuisine)
		}
	}
	return { weekend: isWeekend(date), nearbyKeys, adjacent, nearbyCuisines }
}

const varietyPenalty = (facets: Facets, ctx: DayContext): number => {
	let penalty = 0
	for (const near of ctx.adjacent) {
		if (clashes(facets.protein, near.facets.protein)) {
			penalty += W_PROTEIN_CLASH
		}
		if (clashes(facets.carb, near.facets.carb)) penalty += W_CARB_CLASH
	}
	// Counted once however many nearby days match — three days of Italian is
	// one problem, not three.
	if (facets.cuisine && ctx.nearbyCuisines.has(facets.cuisine)) {
		penalty += W_CUISINE_CLASH
	}
	return penalty
}

// Half strength: a quick meal on Sat–Sun is fine, a three-hour cook on a
// weekday is not.
const WEEKEND_PULL = 0.5

// How well a candidate suits the day, centred on zero in [-1, 1]. A recipe is
// scored on heaviness; a hand-typed meal on how its past appearances divide
// between weekdays and weekends, weighted by `confidence`.
const dayFit = (c: Candidate, weekend: boolean): number => {
	if (c.history) {
		const match = weekend
			? 2 * c.history.weekendRate - 1
			: 1 - 2 * c.history.weekendRate
		return c.history.confidence * match * (weekend ? WEEKEND_PULL : 1)
	}
	const h = c.heaviness ?? UNKNOWN_HEAVINESS
	return weekend ? (2 * h - 1) * WEEKEND_PULL : 1 - 2 * h
}

// Apr–Sep is summer here, Oct–Mar winter. `all_year` always matches.
const seasonMismatch = (date: MealDate, season: Season): boolean => {
	if (season === 'all_year') return false
	const month = Number(date.slice(5, 7))
	return month >= 4 && month <= 9 ? season !== 'summer' : season !== 'winter'
}

// 0 = cooked recently, 1 = long overdue or never cooked. A hand-typed meal is
// measured against its own interval; a recipe has none, so it uses a fixed
// number of days.
const rotationBonus = (date: MealDate, c: Candidate): number => {
	if (c.lastPlanned === null) return 1
	const since = daysBetween(c.lastPlanned, date)
	const full = c.history ? c.history.intervalDays : ROTATION_FULL_DAYS
	return Math.min(1, Math.max(0, since / full))
}

const score = (
	date: MealDate,
	c: Candidate,
	ctx: DayContext,
	penalty: number,
): number => {
	let s = W_DAY_FIT * dayFit(c, ctx.weekend) - penalty
	if (c.tags && seasonMismatch(date, c.tags.season)) s -= W_SEASON_MISMATCH
	if (ctx.weekend && c.tags?.batchCook === 'yes') s += W_BATCH_COOK
	s += W_ROTATION * rotationBonus(date, c)
	s += Math.random() * W_JITTER
	return s
}

const pickFor = (
	date: MealDate,
	pool: Candidate[],
	ctx: DayContext,
	used: ReadonlySet<string>,
	rejected: ReadonlySet<string> | undefined,
): Candidate | null => {
	const eligible = pool.filter(
		(c) =>
			!used.has(c.key) &&
			!ctx.nearbyKeys.has(c.key) &&
			!rejected?.has(c.key),
	)
	if (eligible.length === 0) return null

	const penalties = new Map<string, number>()
	for (const c of eligible)
		penalties.set(c.key, varietyPenalty(c.facets, ctx))
	const tagged = eligible.filter((c) => c.tags)
	const mean =
		tagged.length === 0
			? 0
			: tagged.reduce((t, c) => t + (penalties.get(c.key) ?? 0), 0) /
				tagged.length

	let best: Candidate | null = null
	let bestScore = -Infinity
	for (const c of eligible) {
		// A hand-typed meal only knows the facets its title gave away, so it
		// would dodge whatever it can't see and get picked more often than the
		// recipes. Charge it the pool's average where its own penalty is
		// lower, and its own where that's worse.
		const own = penalties.get(c.key) ?? 0
		const s = score(date, c, ctx, c.tags ? own : Math.max(own, mean))
		if (s > bestScore) {
			bestScore = s
			best = c
		}
	}
	return best
}

// One day of a proposal. `key` identifies the candidate it came from.
export type Suggestion = {
	date: MealDate
	title: string
	recipeId: RecipeId | null
	key: string
}

export type SuggestInput = {
	// Days to fill.
	fill: MealDate[]
	// Days already proposed. Returned unchanged, but their candidates are
	// still excluded and they still count as neighbours.
	pinned?: Suggestion[]
	// Every planned day we know about, past and future.
	plan: MealPlanDay[]
	recipes: Recipe[]
	appearances: Map<string, MealAppearances>
	// Candidate keys already turned down for a given day. Adding to this moves
	// to the next-best candidate instead of alternating between two.
	rejected?: ReadonlyMap<MealDate, ReadonlySet<string>>
}

export type Proposal = {
	// Pinned and newly filled days together, in date order.
	suggestions: Suggestion[]
	// Days there was nothing left to fill with.
	unfilled: number
}

export const suggest = (input: SuggestInput): Proposal => {
	const { plan, recipes, appearances } = input
	const fill = [...input.fill].sort((a, b) => a.localeCompare(b))
	const pinned = input.pinned ?? []

	const index = indexRecipes(recipes)
	const pool = buildPool(recipes, appearances, plan, index)
	const byKey = new Map(pool.map((c) => [c.key, c]))

	// The plan as the proposal would leave it: what's stored, minus the days
	// being replaced, plus the suggestions held or made as we go. A day about
	// to be overwritten must not affect its neighbours' scores.
	const replacing = new Set<MealDate>([...fill, ...pinned.map((p) => p.date)])
	const planned = new Map<MealDate, PlannedMeal>()
	for (const day of plan) {
		if (replacing.has(day.date)) continue
		const meal = resolvePlanned(day, index)
		if (meal) planned.set(day.date, meal)
	}
	for (const p of pinned) {
		const c = byKey.get(p.key)
		planned.set(p.date, {
			key: p.key,
			facets: c?.facets ?? inferFacets(p.key),
		})
	}

	const used = new Set<string>(pinned.map((p) => p.key))
	const made: Suggestion[] = []

	for (const date of fill) {
		const ctx = buildContext(date, planned)
		const pick = pickFor(date, pool, ctx, used, input.rejected?.get(date))
		if (!pick) continue
		used.add(pick.key)
		planned.set(date, { key: pick.key, facets: pick.facets })
		made.push({
			date,
			title: pick.title,
			recipeId: pick.recipeId,
			key: pick.key,
		})
	}

	return {
		suggestions: [...pinned, ...made].sort((a, b) =>
			a.date.localeCompare(b.date),
		),
		unfilled: fill.length - made.length,
	}
}
